"use client";

import { useState } from "react";
import type { PlanRow } from "@/lib/db/queries/plans";
import type { PlanFeatureCatalogEntry } from "@/lib/db/queries/plan-features-catalog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PlanEditModal } from "./plan-edit-modal";

const priceFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const numFmt = new Intl.NumberFormat("fr-FR");

// Convention métier : -1 = illimité. Affiché ∞ pour faciliter la lecture.
function fmtQuota(n: number): string {
  if (n === -1) return "∞";
  return numFmt.format(n);
}

// Cf. plan-edit-modal.tsx : mysql2 décode les colonnes JSON en JS direct
// (array/object). On accepte string en plus pour rester défensif.
function parseFeatures(raw: unknown): string[] {
  if (raw == null) return [];
  let parsed: unknown = raw;
  if (typeof raw === "string") {
    try {
      parsed = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (Array.isArray(parsed))
    return parsed.filter((x): x is string => typeof x === "string");
  if (parsed && typeof parsed === "object") {
    return Object.entries(parsed as Record<string, unknown>)
      .filter(([, v]) => v === true || v === 1 || v === "1")
      .map(([k]) => k);
  }
  return [];
}

export function PlansGrid({
  rows,
  featureCatalog,
}: {
  rows: PlanRow[];
  featureCatalog: PlanFeatureCatalogEntry[];
}) {
  const [editing, setEditing] = useState<PlanRow | null>(null);

  // Map code → label pour rendre les feature codes en libellé FR sur les
  // cartes (sinon on affiche un code technique).
  const labelByCode = new Map(
    featureCatalog.map((f) => [f.code, f.label] as const),
  );

  // Plan actuellement "Le plus populaire" — passé à la modale pour qu'elle
  // puisse demander une confirmation explicite si l'admin déplace le badge.
  const currentlyHighlighted = rows.find((p) => p.highlighted === 1) ?? null;

  return (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {rows.map((p) => {
          const features = parseFeatures(p.features);
          return (
            <article
              key={p.id}
              className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-5"
            >
              <header className="flex items-baseline justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">{p.name}</h2>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
                    {p.code}
                  </p>
                </div>
                <Badge tone={p.visible ? "success" : "neutral"}>
                  {p.visible ? "Visible" : "Caché"}
                </Badge>
              </header>

              {p.description && (
                <p className="text-xs text-muted-foreground">{p.description}</p>
              )}

              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-semibold tabular-nums">
                  {priceFmt.format(Number(p.monthly_price))}
                </span>
                <span className="text-xs text-muted-foreground">/mois</span>
                <span className="ml-auto text-xs text-muted-foreground tabular-nums">
                  {priceFmt.format(Number(p.yearly_price))}/an
                </span>
              </div>

              <dl className="grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs">
                <div>
                  <dt className="text-muted-foreground">Techniciens</dt>
                  <dd className="font-medium tabular-nums">
                    {fmtQuota(p.max_technicians)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Administrateurs</dt>
                  <dd className="font-medium tabular-nums">
                    {fmtQuota(p.max_admins)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Sites</dt>
                  <dd className="font-medium tabular-nums">
                    {fmtQuota(p.max_sites)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Machines</dt>
                  <dd className="font-medium tabular-nums">
                    {fmtQuota(p.max_machines)}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">Templates</dt>
                  <dd className="font-medium tabular-nums">
                    {fmtQuota(p.max_templates)}
                  </dd>
                </div>
              </dl>

              {features.length > 0 && (
                <div className="flex flex-wrap gap-1.5 border-t border-border pt-3">
                  {features.map((f) => (
                    <span
                      key={f}
                      className="inline-block rounded border border-border bg-surface-2 px-2 py-0.5 text-[11px] text-foreground"
                      title={f}
                    >
                      {labelByCode.get(f) ?? f}
                    </span>
                  ))}
                </div>
              )}

              <footer className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-3 text-xs">
                <span className="text-muted-foreground">
                  <span className="font-semibold tabular-nums text-foreground">
                    {p.active_subscriptions}
                  </span>{" "}
                  abonnement{p.active_subscriptions > 1 ? "s" : ""} actif
                  {p.active_subscriptions > 1 ? "s" : ""}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(p)}
                >
                  Modifier
                </Button>
              </footer>
            </article>
          );
        })}
      </div>

      <PlanEditModal
        plan={editing}
        featureCatalog={featureCatalog}
        currentlyHighlighted={currentlyHighlighted}
        open={editing !== null}
        onClose={() => setEditing(null)}
      />
    </>
  );
}
